import React from 'react';
import { Icon } from '@iconify/react';
import { LoyaltyProgram } from '@/types/loyalty';

interface Props {
  formData: LoyaltyProgram;
  onChange: (patch: Partial<LoyaltyProgram>) => void;
  disabled?: boolean;
}

const formatThousand = (val: number | undefined | null): string => {
  if (val === undefined || val === null || isNaN(val)) return '';
  if (val === 0) return '';
  return Math.round(val).toLocaleString('id-ID');
};

const parseDigits = (raw: string): number => {
  const digits = raw.replace(/\D/g, '');
  return digits ? Number(digits) : 0;
};

export const PointsSettingsForm: React.FC<Props> = ({
  formData,
  onChange,
  disabled = false,
}) => {
  const toggleSource = (sourceKey: string) => {
    const current = formData.points_sources || [];
    if (current.includes(sourceKey)) {
      if (current.length === 1) return;
      onChange({ points_sources: current.filter((s) => s !== sourceKey) });
    } else {
      onChange({ points_sources: [...current, sourceKey] });
    }
  };

  const sourcesList = [
    { id: 'booking', label: 'Booking lapangan', icon: 'ph:soccer-ball' },
    { id: 'rental', label: 'Sewa & layanan', icon: 'ph:handshake' },
    { id: 'product', label: 'Produk', icon: 'ph:package' },
  ];

  const expiryMonths = formData.points_expiry_months || 12;

  return (
    <div className="space-y-5">
      {/* Row 1: Belanja per 1 poin & Nilai 1 poin */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Belanja per 1 poin
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Kelipatan nominal transaksi
            </p>
          </div>
          <div className="flex items-center h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden focus-within:border-[#FC480D] transition-colors">
            <span className="h-full px-3.5 bg-[#F8F9FA] border-r border-[#E0E6ED] flex items-center text-xs font-medium text-[#8C8C8C] select-none">
              Rp
            </span>
            <input
              type="text"
              inputMode="numeric"
              disabled={disabled}
              value={formatThousand(formData.points_spend_per_point)}
              onChange={(e) =>
                onChange({ points_spend_per_point: parseDigits(e.target.value) })
              }
              placeholder="10.000"
              className="w-full h-full px-3.5 text-xs sm:text-[13px] font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none disabled:bg-[#F8F9FA]"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Nilai 1 poin
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Saat ditukar jadi potongan
            </p>
          </div>
          <div className="flex items-center h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden focus-within:border-[#FC480D] transition-colors">
            <span className="h-full px-3.5 bg-[#F8F9FA] border-r border-[#E0E6ED] flex items-center text-xs font-medium text-[#8C8C8C] select-none">
              Rp
            </span>
            <input
              type="text"
              inputMode="numeric"
              disabled={disabled}
              value={formatThousand(formData.points_redemption_rate)}
              onChange={(e) =>
                onChange({ points_redemption_rate: parseDigits(e.target.value) })
              }
              placeholder="100"
              className="w-full h-full px-3.5 text-xs sm:text-[13px] font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none disabled:bg-[#F8F9FA]"
            />
          </div>
        </div>
      </div>

      {/* Row 2: Poin didapat dari */}
      <div className="space-y-2">
        <div>
          <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
            Poin didapat dari
          </label>
          <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
            Transaksi yang menghasilkan poin
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {sourcesList.map((item) => {
            const isChecked = (formData.points_sources || []).includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                disabled={disabled}
                onClick={() => toggleSource(item.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-xs transition-all cursor-pointer ${
                  isChecked
                    ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D] font-semibold'
                    : 'border-[#E0E6ED] bg-white text-[#8C8C8C] hover:text-[#0E1726] hover:bg-gray-50 font-medium'
                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <Icon icon={item.icon} className="w-3.5 h-3.5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 3: Minimum tukar */}
      <div className="space-y-1.5">
        <div>
          <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
            Minimum tukar
          </label>
          <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
            Poin minimal sebelum bisa ditukar
          </p>
        </div>
        <div className="flex items-center h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden focus-within:border-[#FC480D] transition-colors">
          <input
            type="text"
            inputMode="numeric"
            disabled={disabled}
            value={formatThousand(formData.points_min_redeem)}
            onChange={(e) =>
              onChange({ points_min_redeem: parseDigits(e.target.value) })
            }
            placeholder="100"
            className="w-full h-full px-3.5 text-xs sm:text-[13px] font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none disabled:bg-[#F8F9FA]"
          />
          <span className="h-full px-3.5 bg-[#F8F9FA] border-l border-[#E0E6ED] flex items-center text-xs font-medium text-[#8C8C8C] select-none">
            poin
          </span>
        </div>
      </div>

      {/* Divider + Poin kedaluwarsa */}
      <div className="border-t border-[#F0F2F5] pt-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <span className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Poin kedaluwarsa
            </span>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Poin hangus jika tidak dipakai
            </p>
          </div>
          <label
            className={`relative inline-flex items-center ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              disabled={disabled}
              checked={formData.points_has_expiry}
              onChange={(e) => onChange({ points_has_expiry: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-10 h-5 bg-[#E0E6ED] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FC480D]" />
          </label>
        </div>

        {formData.points_has_expiry && (
          <div className="space-y-1.5">
            <div>
              <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
                Masa berlaku
              </label>
              <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
                Dihitung sejak poin didapat
              </p>
            </div>

            <div className="flex items-center justify-between h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden">
              <button
                type="button"
                disabled={disabled || expiryMonths <= 1}
                onClick={() =>
                  onChange({ points_expiry_months: Math.max(1, expiryMonths - 1) })
                }
                className="w-10 h-full border-r border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                <Icon icon="ph:minus" className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
                {expiryMonths} bulan
              </span>
              <button
                type="button"
                disabled={disabled || expiryMonths >= 36}
                onClick={() =>
                  onChange({ points_expiry_months: Math.min(36, expiryMonths + 1) })
                }
                className="w-10 h-full border-l border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                <Icon icon="ph:plus" className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsSettingsForm;
